<template>
  <div class="campaign-report-date">
    <span class="campaign-report-date-label">{{ label }}</span>
    <template v-if="locale === 'fa'">
      <input
        :value="draft"
        required
        type="text"
        inputmode="text"
        dir="ltr"
        :disabled="disabled"
        :aria-label="label"
        :aria-invalid="invalid ? 'true' : 'false'"
        :aria-describedby="hintId"
        :data-campaign-field="field"
        placeholder="۱۴۰۵/۰۷/۲۵"
        @input="typed($event.target.value)"
        @change="commitDraft"
        @keydown.enter.prevent="commitDraft"
      >
      <small
        :id="hintId"
        :role="invalid ? 'alert' : undefined"
      >{{ invalid ? 'تاریخ درست نیست. نمونه: ۱۴۰۵/۰۷/۲۵' : 'سال/ماه/روز را وارد کنید یا از تقویم انتخاب کنید.' }}</small>
      <div class="campaign-date-actions">
        <button
          type="button"
          :disabled="disabled"
          :aria-expanded="calendarOpen ? 'true' : 'false'"
          @click="calendarOpen = !calendarOpen"
        >
          انتخاب از تقویم
        </button>
        <button
          v-if="draft"
          type="button"
          :disabled="disabled"
          @click="clear"
        >
          پاک کردن تاریخ
        </button>
      </div>
      <persian-date-picker
        ref="picker"
        :value="pickerValue"
        locale="fa"
        type="date"
        mode="single"
        :column="1"
        format="YYYY-MM-DD"
        input-format="jYYYY/jMM/jDD"
        display-format="jD jMMMM jYYYY"
        :auto-submit="false"
        :clearable="false"
        :modal="true"
        :show="calendarOpen && !disabled"
        click-on="none"
        :input-readonly="true"
        input-style="display: none"
        input-tabindex="-1"
        input-aria-hidden="true"
        @setDate="selected"
        @close="calendarOpen = false"
      ><template #icon>
        <span />
      </template></persian-date-picker>
    </template>
    <input
      v-else
      :value="value"
      type="date"
      required
      :disabled="disabled"
      :aria-label="label"
      :data-campaign-field="field"
      @change="selected($event.target.value)"
    >
  </div>
</template>
<script>
import PersianDatePicker from '@alireza-ab/vue-persian-datepicker'
import { normalizePickerTime, jalaliInput, jalaliGregorian } from './time'

function displayDate(value) { return value ? jalaliInput(`${value}T00:00`).split(' ')[0] : '' }
function gregorianDate(value) { return value ? jalaliGregorian(`${value} 00:00`).slice(0, 10) : '' }

export default {
  components: { PersianDatePicker },
  props: {
    value: { type: String, default: '' },
    locale: { type: String, default: 'fa' },
    label: { type: String, required: true },
    field: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
  },
  data() { return { draft: displayDate(this.value), invalid: false, calendarOpen: false } },
  computed: {
    pickerValue() { try { return gregorianDate(this.draft) } catch (_) { return '' } },
    hintId() { return `campaign-report-calendar-hint-${this.field || 'end'}` },
  },
  watch: {
    value(next) { if (next !== 'INVALID_REPORT_DATE') { this.invalid = false; this.draft = displayDate(next) } },
    calendarOpen(open) { if (open && this.pickerValue && this.$refs.picker) this.$refs.picker.setDate(this.pickerValue) },
  },
  methods: {
    typed(value) {
      this.draft = value
      this.commitDraft()
    },
    commitDraft() {
      if (this.disabled) return
      try {
        const normalized = gregorianDate(this.draft)
        this.invalid = false
        if (normalized !== this.value) this.$emit('change', normalized)
      } catch (_) {
        this.invalid = true
        this.$emit('change', 'INVALID_REPORT_DATE')
      }
    },
    clear() { if (!this.disabled) { this.draft = ''; this.invalid = false; this.calendarOpen = false; this.$emit('change', '') } },
    selected(value) {
      if (this.disabled) return
      const normalized = normalizePickerTime(value)
      // Report dates identify UTC buckets; calendar conversion must not shift their day.
      if (normalized === this.value) return
      this.invalid = false
      this.draft = displayDate(normalized)
      this.$emit('change', normalized)
    },
  },
}
</script>
<style scoped>
.campaign-report-date { min-width: 0; width: 100%; }
.campaign-date-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 6px; }
.campaign-date-actions button { width: auto; padding: 6px 10px; }
.campaign-report-date ::v-deep .pdp { width: 100%; }
.campaign-report-date ::v-deep .pdp-group { display: none; }
.campaign-report-date ::v-deep .pdp-picker { max-width: calc(100vw - 24px); max-height: calc(100vh - 32px); overflow: auto; z-index: 1061; }
.campaign-report-date ::v-deep .pdp-overlay { z-index: 1060; }
.campaign-report-date input:focus, .campaign-date-actions button:focus { outline: 2px solid #247782; outline-offset: 2px; }
</style>
